import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronDown, ChevronRight, ChevronsDownUp, ChevronsUpDown, Code2, Copy, FileCode2, Folder,
  FolderOpen, Globe2, Plus, Save, Settings2, Trash2,
} from 'lucide-react';
import { api, jsonBody } from './api';
import { CodeEditor } from './CodeEditor';
import { Modal } from './Modal';
import { Select } from './Select';
import { ConfirmDialog } from './Dialogs';

type Script = { id: string; path: string; name: string; description: string; language: string; public: boolean; active_version: number | null; created_at: string; updated_at: string };
type Version = { id: string; version: number; sha256: string; created_at: string; content?: string };
type TreeNode = { name: string; path: string; kind: 'folder' | 'file'; script?: Script; children: TreeNode[] };
type Notify = (type: 'success' | 'error', message: string) => void;
const message = (error: unknown) => error instanceof Error ? error.message : '操作失败';

function buildTree(scripts: Script[]): TreeNode[] {
  const root: TreeNode[] = [];
  for (const script of scripts) {
    const parts = script.path.split('/').filter(Boolean);
    let level = root;
    let prefix = '';
    parts.forEach((part, index) => {
      prefix = prefix ? `${prefix}/${part}` : part;
      const last = index === parts.length - 1;
      let node = level.find((item) => item.name === part && item.kind === (last ? 'file' : 'folder'));
      if (!node) {
        node = { name: part, path: prefix, kind: last ? 'file' : 'folder', script: last ? script : undefined, children: [] };
        level.push(node);
      }
      if (!last) level = node.children;
    });
  }
  const sort = (nodes: TreeNode[]) => {
    nodes.sort((a, b) => a.kind === b.kind ? a.name.localeCompare(b.name) : a.kind === 'folder' ? -1 : 1);
    nodes.forEach((node) => sort(node.children));
  };
  sort(root);
  return root;
}

function collectFolders(nodes: TreeNode[], target: Set<string>) {
  for (const node of nodes) {
    if (node.kind === 'folder') {
      target.add(node.path);
      collectFolders(node.children, target);
    }
  }
}

function filterScripts(scripts: Script[], query: string): Script[] {
  const value = query.trim().toLowerCase();
  if (!value) return scripts;
  return scripts.filter((script) => script.path.toLowerCase().includes(value) || script.description.toLowerCase().includes(value));
}

/** 只存折叠的目录：新增目录默认展开，“全部展开”即清空。 */
const COLLAPSED_FOLDERS_STORAGE_KEY = 'edgedock.global-scripts.collapsed-folders';
const SELECTED_SCRIPT_STORAGE_KEY = 'edgedock.global-scripts.selected-id';

function readCollapsedFolders(): Set<string> {
  try {
    const raw = window.localStorage.getItem(COLLAPSED_FOLDERS_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((value): value is string => typeof value === 'string'));
  } catch {
    return new Set();
  }
}

function readSelectedScriptID(): string {
  try {
    return window.localStorage.getItem(SELECTED_SCRIPT_STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
}

function sameFolderSet(left: Set<string>, right: Set<string>): boolean {
  if (left.size !== right.size) return false;
  for (const value of left) {
    if (!right.has(value)) return false;
  }
  return true;
}

/** 打开脚本所在的所有父目录，避免新建/改名后选中项被折叠隐藏。 */
function revealFolders(collapsed: Set<string>, path: string): Set<string> {
  const parts = path.split('/').filter(Boolean);
  if (parts.length <= 1) return collapsed;
  let next: Set<string> | null = null;
  let prefix = '';
  for (const part of parts.slice(0, -1)) {
    prefix = prefix ? `${prefix}/${part}` : part;
    if (collapsed.has(prefix)) {
      next ??= new Set(collapsed);
      next.delete(prefix);
    }
  }
  return next ?? collapsed;
}

function withoutFolders(collapsed: Set<string>, folders: Set<string>): Set<string> {
  let next: Set<string> | null = null;
  for (const folder of folders) {
    if (collapsed.has(folder)) {
      next ??= new Set(collapsed);
      next.delete(folder);
    }
  }
  return next ?? collapsed;
}

export function GlobalScripts({ notify }: { notify: Notify }) {
  const [scripts, setScripts] = useState<Script[]>([]);
  const [selectedID, setSelectedID] = useState('');
  const [content, setContent] = useState('');
  const [savedContent, setSavedContent] = useState('');
  const [language, setLanguage] = useState('shell');
  const [scriptPath, setScriptPath] = useState('');
  const [description, setDescription] = useState('');
  const [activeVersion, setActiveVersion] = useState<number | null>(null);
  const [versions, setVersions] = useState<Version[]>([]);
  const [viewVersion, setViewVersion] = useState<number | null>(null);
  const [query, setQuery] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(readCollapsedFolders);
  const [busy, setBusy] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState({ path: '', description: '', language: 'shell', public: false });
  const [form, setForm] = useState({ path: '', description: '', language: 'shell', public: false, content: '#!/bin/sh\n' });

  const selected = scripts.find((script) => script.id === selectedID) ?? null;
  const filtered = useMemo(() => filterScripts(scripts, query), [query, scripts]);
  const tree = useMemo(() => buildTree(filtered), [filtered]);
  const dirty = content !== savedContent;
  const treeBodyRef = useRef<HTMLDivElement>(null);

  // 记住每个目录的折叠状态；存储不可用时只失去记忆能力，不影响展开/收起。
  useEffect(() => {
    try {
      window.localStorage.setItem(COLLAPSED_FOLDERS_STORAGE_KEY, JSON.stringify([...collapsed].sort()));
    } catch {
      // 隐私模式或配额受限。
    }
  }, [collapsed]);

  // 记住当前聚焦的脚本；首次加载完成前不写入，避免清掉待恢复的记录。
  useEffect(() => {
    if (loading) return;
    try {
      if (selectedID) window.localStorage.setItem(SELECTED_SCRIPT_STORAGE_KEY, selectedID);
      else window.localStorage.removeItem(SELECTED_SCRIPT_STORAGE_KEY);
    } catch {
      // 存储不可用时忽略。
    }
  }, [loading, selectedID]);

  // 同一账号在多个标签页打开时，保持目录树折叠状态一致。
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== COLLAPSED_FOLDERS_STORAGE_KEY || event.newValue === event.oldValue) return;
      const next = readCollapsedFolders();
      setCollapsed((current) => (sameFolderSet(current, next) ? current : next));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // 把聚焦脚本滚动到可视区域；目录仍按用户手动折叠状态展示。
  useEffect(() => {
    const body = treeBodyRef.current;
    if (!body) return;
    const active = body.querySelector<HTMLElement>('.ide-tree-row.active');
    if (!active) return;
    const top = active.offsetTop;
    const bottom = top + active.offsetHeight;
    if (top < body.scrollTop) body.scrollTop = Math.max(0, top - 6);
    else if (bottom > body.scrollTop + body.clientHeight) body.scrollTop = bottom - body.clientHeight + 6;
  }, [selectedID, tree, collapsed]);

  const loadScripts = useCallback(async () => {
    const data = await api<{ scripts: Script[] }>('/api/global-scripts');
    setScripts(data.scripts);
    return data.scripts;
  }, []);

  const loadVersions = useCallback(async (scriptID: string) => {
    const data = await api<{ versions: Version[] }>(`/api/global-scripts/${scriptID}/versions`);
    setVersions(data.versions);
    return data.versions;
  }, []);

  const openScript = useCallback(async (script: Script, version?: number, reveal = true) => {
    setBusy(true);
    try {
      const suffix = version ? `?version=${version}` : '';
      const data = await api<{ script: Script; version: Version }>(`/api/global-scripts/${script.id}${suffix}`);
      setSelectedID(script.id);
      if (reveal) setCollapsed((current) => revealFolders(current, script.path));
      setScriptPath(data.script.path);
      setDescription(data.script.description);
      setLanguage(data.script.language);
      setActiveVersion(data.script.active_version);
      setContent(data.version.content ?? '');
      setSavedContent(data.version.content ?? '');
      setViewVersion(data.version.version);
      await loadVersions(script.id);
    } catch (error) { notify('error', message(error)); }
    finally { setBusy(false); }
  }, [loadVersions, notify]);

  useEffect(() => {
    let disposed = false;
    (async () => {
      setLoading(true);
      try {
        const list = await loadScripts();
        if (!disposed && list.length > 0) {
          const remembered = readSelectedScriptID();
          const target = list.find((script) => script.id === remembered) ?? list[0];
          // 恢复上次聚焦的脚本，但不改动用户手动折叠的目录。
          await openScript(target, undefined, false);
        }
      } catch (error) { if (!disposed) notify('error', message(error)); }
      finally { if (!disposed) setLoading(false); }
    })();
    return () => { disposed = true; };
  }, [loadScripts, notify, openScript]);

  async function saveVersion(activate: boolean) {
    if (!selectedID) return;
    setBusy(true);
    try {
      const data = await api<{ version: Version }>(`/api/global-scripts/${selectedID}/versions`, {
        method: 'POST', body: jsonBody({ content, activate }),
      });
      const list = await loadScripts();
      if (activate) {
        const current = list.find((script) => script.id === selectedID);
        if (current) await openScript(current);
      } else {
        setSavedContent(content);
        setViewVersion(data.version.version);
        await loadVersions(selectedID);
      }
      notify('success', activate ? '新版本已保存并激活' : '新版本已保存');
    } catch (error) { notify('error', message(error)); }
    finally { setBusy(false); }
  }

  async function activateVersion() {
    if (!selectedID || !viewVersion) return;
    setBusy(true);
    try {
      await api(`/api/global-scripts/${selectedID}/activate`, { method: 'POST', body: jsonBody({ version: viewVersion }) });
      await loadScripts();
      setActiveVersion(viewVersion);
      notify('success', `已激活 v${viewVersion}`);
    } catch (error) { notify('error', message(error)); }
    finally { setBusy(false); }
  }

  async function create() {
    setBusy(true);
    try {
      const data = await api<{ script: Script }>('/api/global-scripts', { method: 'POST', body: jsonBody(form) });
      setCreateOpen(false);
      setForm({ path: '', description: '', language: 'shell', public: false, content: '#!/bin/sh\n' });
      await loadScripts();
      await openScript(data.script);
      notify('success', '全局脚本已创建');
    } catch (error) { notify('error', message(error)); }
    finally { setBusy(false); }
  }

  async function updateSettings() {
    if (!selectedID) return;
    setBusy(true);
    try {
      const data = await api<{ script: Script }>(`/api/global-scripts/${selectedID}`, {
        method: 'PATCH', body: jsonBody(settings),
      });
      setSettingsOpen(false);
      await loadScripts();
      await openScript(data.script);
      notify('success', '脚本设置已更新');
    } catch (error) { notify('error', message(error)); }
    finally { setBusy(false); }
  }

  function remove() {
    if (!selectedID) return;
    setDeleteOpen(true);
  }

  async function confirmRemove() {
    if (!selectedID) return;
    setBusy(true);
    try {
      await api(`/api/global-scripts/${selectedID}`, { method: 'DELETE' });
      const list = await loadScripts();
      setSelectedID(''); setContent(''); setSavedContent(''); setVersions([]); setViewVersion(null); setActiveVersion(null);
      if (list.length > 0) await openScript(list[0]);
      setDeleteOpen(false);
      notify('success', '脚本已删除');
    } catch (error) { notify('error', message(error)); }
    finally { setBusy(false); }
  }

  async function copyURL() {
    if (!scriptPath) return;
    const pinned = viewVersion && viewVersion !== activeVersion;
    const suffix = pinned ? `?version=${viewVersion}` : '';
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/api/scripts/${scriptPath}${suffix}`);
      const label = pinned ? `v${viewVersion} ` : '';
      notify('success', selected?.public
        ? `${label}公开脚本 URL 已复制，可直接访问`
        : `${label}脚本 URL 已复制，读取时需携带设备 key`);
    } catch { notify('error', '无法复制 URL'); }
  }

  function toggleFolder(path: string) {
    setCollapsed((current) => {
      const next = new Set(current);
      next.has(path) ? next.delete(path) : next.add(path);
      return next;
    });
  }

  function expandAll() {
    setCollapsed(new Set());
  }

  function collapseAll() {
    const next = new Set<string>();
    collectFolders(tree, next);
    setCollapsed(next);
  }

  function updateQuery(value: string) {
    setQuery(value);
    if (!value.trim()) return;
    // 搜索时展开命中路径，否则结果会被折叠的目录挡住。
    const matches = new Set<string>();
    collectFolders(buildTree(filterScripts(scripts, value)), matches);
    setCollapsed((current) => withoutFolders(current, matches));
  }

  function renderTree(nodes: TreeNode[], depth = 0) {
    return nodes.map((node) => {
      if (node.kind === 'folder') {
        const open = !collapsed.has(node.path);
        return <div key={`f:${node.path}`}>
          <button type="button" className="ide-tree-row folder" aria-expanded={open} style={{ paddingLeft: 10 + depth * 14 }} onClick={() => toggleFolder(node.path)}>
            {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            {open ? <FolderOpen size={15} /> : <Folder size={15} />}
            <span>{node.name}</span>
          </button>
          {open && renderTree(node.children, depth + 1)}
        </div>;
      }
      const script = node.script!;
      return <button type="button" key={`s:${script.id}`} className={`ide-tree-row file${script.id === selectedID ? ' active' : ''}`} style={{ paddingLeft: 27 + depth * 14 }} onClick={() => void openScript(script)}>
        <FileCode2 size={14} /><span>{node.name}</span>{script.public && <span className="public-mark" title="公开脚本" aria-label="公开脚本"><Globe2 size={12} /></span>}{script.active_version !== null && <i title={`active v${script.active_version}`} />}
      </button>;
    });
  }

  return <div className="ide-shell" aria-busy={loading || busy}>
    <aside className="ide-tree">
      <header className="ide-tree-head">
        <div><strong>脚本</strong><span>{scripts.length} 个</span></div>
        <div className="ide-tree-actions">
          <button type="button" className="icon-button" title="全部展开" aria-label="全部展开" onClick={expandAll}><ChevronsUpDown size={15} /></button>
          <button type="button" className="icon-button" title="全部折叠" aria-label="全部折叠" onClick={collapseAll}><ChevronsDownUp size={15} /></button>
          <button type="button" className="icon-button" title="新建脚本" aria-label="新建脚本" onClick={() => setCreateOpen(true)}><Plus size={16} /></button>
        </div>
      </header>
      <div className="ide-tree-search"><input value={query} onChange={(event) => updateQuery(event.target.value)} placeholder="搜索脚本或路径" aria-label="搜索脚本" /></div>
      <div className="ide-tree-body" ref={treeBodyRef}>
        {loading && <p className="empty">加载脚本…</p>}
        {!loading && tree.length === 0 && <p className="empty">{query ? '没有匹配脚本' : '还没有全局脚本'}</p>}
        {renderTree(tree)}
      </div>
      <footer className="ide-tree-foot"><Code2 size={13} />所有已激活设备 key 可读；公开脚本无需鉴权</footer>
    </aside>

    <section className="ide-main">
      <header className="ide-toolbar">
        <div className="ide-file-title">
          <strong>{scriptPath || '未选择脚本'}</strong>
          <span>{language}{dirty ? ' · 有未保存修改' : ''}{activeVersion ? ` · active v${activeVersion}` : ''}{selected?.public ? ' · 公开' : ''}</span>
        </div>
        <div className="ide-toolbar-actions">
          <button type="button" className="secondary-button small" disabled={!selectedID} title={viewVersion && viewVersion !== activeVersion ? `复制 v${viewVersion} 的固定版本 URL` : selected?.public ? '公开脚本无需设备 key 或登录即可读取' : '读取时需要设备 key 或登录会话'} onClick={() => void copyURL()}><Copy size={14} />{viewVersion && viewVersion !== activeVersion ? `复制 v${viewVersion} URL` : selected?.public ? '复制公开 URL' : '复制 URL'}</button>
          <button type="button" className="secondary-button small" disabled={!selectedID || busy} onClick={() => void saveVersion(false)}><Save size={14} />保存版本</button>
          <button type="button" className="primary-button small" disabled={!selectedID || busy} onClick={() => void saveVersion(true)}><Save size={14} />保存并激活</button>
          <Select aria-label="查看版本" className="compact-select" value={viewVersion ?? ''} onChange={(event) => { if (selected) void openScript(selected, Number(event.target.value)); }} disabled={!selectedID}>
            {versions.map((version) => <option key={version.version} value={version.version}>v{version.version}</option>)}
          </Select>
          {viewVersion && viewVersion !== activeVersion && <button type="button" className="text-button" disabled={busy} onClick={() => void activateVersion()}>激活 v{viewVersion}</button>}
          <button type="button" className="icon-button" title="脚本设置" aria-label="脚本设置" disabled={!selectedID} onClick={() => { if (selected) { setSettings({ path: scriptPath, description, language, public: selected.public }); setSettingsOpen(true); } }}><Settings2 size={16} /></button>
          <button type="button" className="icon-button danger" title="删除脚本" aria-label="删除脚本" disabled={!selectedID} onClick={() => void remove()}><Trash2 size={16} /></button>
        </div>
      </header>
      <div className="ide-editor">
        {!selectedID && <div className="tenant-empty"><FileCode2 size={32} /><strong>选择左侧脚本</strong><span>或点击 + 新建全局脚本。</span></div>}
        {selectedID && <CodeEditor value={content} language={language} onChange={setContent} height="100%" />}
      </div>
      <footer className="ide-status">
        <span>{scriptPath || '—'}</span>
        <span>{viewVersion ? `查看 v${viewVersion}` : ''}{activeVersion ? ` · active v${activeVersion}` : ''}</span>
        <span>{dirty ? '未保存' : '已保存'}</span>
      </footer>
    </section>

    {createOpen && <Modal title="新建全局脚本" subtitle="路径支持文件夹，例如 a/1；创建后生成 v1。" onClose={() => setCreateOpen(false)}>
      <form className="device-form" onSubmit={(event) => { event.preventDefault(); void create(); }}>
        <label><span>路径</span><input autoFocus value={form.path} onChange={(event) => setForm({ ...form, path: event.target.value })} placeholder="global-health-check 或 a/1" /></label>
        <label><span>描述</span><input value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} /></label>
        <label><span>语言</span><Select aria-label="语言" value={form.language} onChange={(event) => setForm({ ...form, language: event.target.value })}><option value="shell">Shell</option><option value="python">Python</option><option value="javascript">JavaScript</option></Select></label>
        <label className="public-toggle"><div><strong>公开脚本</strong><span>开启后任何人无需设备 key 或登录即可读取；请勿写入密钥。</span></div><input type="checkbox" checked={form.public} onChange={(event) => setForm({ ...form, public: event.target.checked })} /></label>
        <CodeEditor value={form.content} language={form.language} onChange={(value) => setForm({ ...form, content: value })} height="220px" />
        <footer><button type="button" className="secondary-button" onClick={() => setCreateOpen(false)}>取消</button><button type="submit" className="primary-button" disabled={busy || !form.path.trim()}>创建</button></footer>
      </form>
    </Modal>}

    {settingsOpen && selected && <Modal title="脚本设置" subtitle={scriptPath} onClose={() => setSettingsOpen(false)}>
      <form className="device-form" onSubmit={(event) => { event.preventDefault(); void updateSettings(); }}>
        <label><span>路径</span><input value={settings.path} onChange={(event) => setSettings({ ...settings, path: event.target.value })} /></label>
        <label><span>描述</span><input value={settings.description} onChange={(event) => setSettings({ ...settings, description: event.target.value })} /></label>
        <label><span>语言</span><Select aria-label="语言" value={settings.language} onChange={(event) => setSettings({ ...settings, language: event.target.value })}><option value="shell">Shell</option><option value="python">Python</option><option value="javascript">JavaScript</option></Select></label>
        <label className="public-toggle"><div><strong>公开脚本</strong><span>开启后任何人无需设备 key 或登录即可读取该脚本；请勿写入密钥。</span></div><input type="checkbox" checked={settings.public} onChange={(event) => setSettings({ ...settings, public: event.target.checked })} /></label>
        <div className="ide-version-list">
          <strong>版本</strong>
          {versions.map((version) => <button type="button" key={version.version} className={`ide-version-row${version.version === activeVersion ? ' active' : ''}`} onClick={() => void openScript(selected, version.version)}>
            <span>v{version.version}</span><code>{version.sha256.slice(0, 12)}…</code><span>{new Date(version.created_at).toLocaleString('zh-CN', { hour12: false })}</span>
          </button>)}
        </div>
        <footer><button type="button" className="secondary-button" onClick={() => setSettingsOpen(false)}>取消</button><button type="submit" className="primary-button" disabled={busy || !settings.path.trim()}>保存设置</button></footer>
      </form>
    </Modal>}

    {deleteOpen && <ConfirmDialog
      title="删除全局脚本"
      message="删除该全局脚本及全部版本？此操作不可撤销。"
      confirmLabel="删除"
      danger
      busy={busy}
      onConfirm={() => void confirmRemove()}
      onCancel={() => setDeleteOpen(false)}
    />}
  </div>;
}
